import { useState } from "react";
import { Navigate, NavLink, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LayoutGrid, CreditCard, Users, Megaphone, BarChart3, MapPin, UserCog, Wallet, Settings, LogOut, ChevronDown, Menu, ShieldCheck, X as XIcon, CalendarClock, ScanLine } from "lucide-react";
import { BrandMark } from "./BrandMark";
import { useAuth } from "../context/AuthContext";
import { FirstRunTour } from "./FirstRunTour";

// Nav items are tagged with the roles that see them so we can keep the
// merchant sidebar tight (only the 6 things a Done-For-You merchant needs)
// while superadmin still sees the full toolset.
const NAV = [
  ["Overview", "/app", LayoutGrid, "all"],
  ["Scanning", "/app/scanner", ScanLine, "all"],
  ["Programs", "/app/programs", CreditCard, "all"],
  ["Customers", "/app/customers", Users, "all"],
  ["Campaigns", "/app/campaigns", Megaphone, "all"],
  ["Analytics", "/app/analytics", BarChart3, "all"],
  ["Locations", "/app/locations", MapPin, "admin"],
  ["Team", "/app/team", UserCog, "admin"],
  ["Billing", "/app/billing", Wallet, "admin"],
  ["Settings", "/app/settings", Settings, "all"],
];

export function AppLayout({ children }) {
  const { user, loading, activeOrg, switchOrg, logout, exitImpersonation } = useAuth();
  const [open, setOpen] = useState(false);
  const [orgMenu, setOrgMenu] = useState(false);
  const nav = useNavigate();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#F6F3EC]"><div className="animate-pulse font-display font-bold">BackPocket</div></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!activeOrg && !user.is_superadmin && !user.is_sales) return <Navigate to="/onboarding" replace />;

  const isSalesOnly = user.is_sales && !user.is_superadmin;
  if (isSalesOnly && !window.location.pathname.startsWith("/app/sales")) return <Navigate to="/app/sales" replace />;
  const orgs = user.organisations || [];
  const navItem = ({ isActive }) => `flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-[13.5px] font-medium transition-all duration-200 ${isActive ? "bg-[#C8FF4D] text-[#0B0D10] font-bold shadow-[0_2px_12px_rgba(200,255,77,0.3)]" : "text-white/65 hover:bg-white/10 hover:text-white hover:translate-x-0.5"}`;
  const internalItem = ({ isActive }) => `flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-[13.5px] font-medium transition-all duration-200 ${isActive ? "bg-[#6474FF] text-white font-bold" : "text-white/65 hover:bg-white/10 hover:text-white hover:translate-x-0.5"}`;
  const initials = (user.name || user.email || "?").split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  const pageLabel = (window.location.pathname.split("/")[2] || "overview").replace("-", " ");
  return (
    <div className="min-h-screen bg-[#F4F1E9] flex">
      <aside className={`fixed lg:sticky top-0 z-40 h-screen w-[260px] bg-[#0B0D10] text-white flex-col border-r border-white/10 ${open ? "flex" : "hidden lg:flex"}`} data-testid="app-sidebar">
        <div className="px-5 py-5 flex items-center justify-between border-b border-white/10">
          <div className="flex items-center gap-2.5"><BrandMark size={28} tone="light" /><span className="font-display font-extrabold text-[17px]">BackPocket</span></div>
          <button onClick={() => setOpen(false)} className="lg:hidden h-8 w-8 grid place-items-center rounded-full hover:bg-white/10" data-testid="sidebar-close"><XIcon size={16} /></button>
        </div>
        {!isSalesOnly && (
        <div className="px-4 pt-4 pb-2 relative">
          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/35 px-1 mb-2">Business</div>
          <button data-testid="org-switcher" onClick={() => setOrgMenu(!orgMenu)} className="w-full flex items-center justify-between rounded-xl bg-white/5 border border-white/10 hover:border-white/25 px-3 py-2.5 text-[13.5px] transition-colors">
            <span className="truncate font-medium">{activeOrg?.name || "Select business"}</span>
            <ChevronDown size={15} className="text-white/50" />
          </button>
          {orgMenu && (
            <div className="absolute left-4 right-4 mt-1 bg-[#1A1D22] rounded-xl border border-white/10 p-1 z-50 shadow-2xl backdrop-blur-md">
              {orgs.map((o) => (
                <button key={o.id} onClick={() => { switchOrg(o); setOrgMenu(false); }} className="w-full text-left px-3 py-2 rounded-lg hover:bg-white/10 text-sm">{o.name}</button>
              ))}
              {user.is_superadmin && <Link to="/onboarding" className="block px-3 py-2 rounded-lg hover:bg-white/10 text-sm text-[#C8FF4D]">+ New business</Link>}
            </div>
          )}
        </div>
        )}
        <nav className="flex-1 px-3 pt-3 space-y-0.5 overflow-y-auto">
          {!isSalesOnly && <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/35 px-2 pb-2">Workspace</div>}
          {!isSalesOnly && NAV.filter(([, , , role]) => role === "all" || user.is_superadmin).map(([label, to, Icon]) => (
            <NavLink key={to} to={to} end={to === "/app"} onClick={() => setOpen(false)} data-testid={`nav-${label.toLowerCase()}`} className={navItem}>
              <Icon size={17} strokeWidth={2} />{label}
            </NavLink>
          ))}
          {(user.is_superadmin || user.is_sales) && (
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/35 px-2 pt-5 pb-2">Internal</div>
          )}
          {(user.is_superadmin || user.is_sales) && (
            <NavLink to="/app/sales" onClick={() => setOpen(false)} data-testid="nav-sales" className={internalItem}>
              <CalendarClock size={17} strokeWidth={2} />Sales desk
            </NavLink>
          )}
          {user.is_superadmin && (
            <NavLink to="/app/admin" end onClick={() => setOpen(false)} data-testid="nav-admin" className={internalItem}>
              <UserCog size={17} strokeWidth={2} />Super Admin
            </NavLink>
          )}
          {user.is_superadmin && (
            <NavLink to="/app/admin/staff" onClick={() => setOpen(false)} data-testid="nav-staff" className={internalItem}>
              <Users size={17} strokeWidth={2} />Sales staff
            </NavLink>
          )}
        </nav>
        <div className="p-3 border-t border-white/10">
          <div className="flex items-center gap-3 px-2 py-2">
            <span className="h-9 w-9 rounded-full bg-[#C8FF4D] text-[#0B0D10] grid place-items-center font-display font-extrabold text-[12px] shrink-0">{initials}</span>
            <div className="min-w-0 flex-1"><div className="font-medium text-[13px] truncate">{user.name}</div><div className="text-white/40 text-[11.5px] truncate">{user.email}</div></div>
            <button data-testid="logout-btn" title="Log out" onClick={() => { logout(); window.location.href = "/"; }} className="h-8 w-8 rounded-full grid place-items-center text-white/50 hover:text-white hover:bg-white/10 transition-colors"><LogOut size={15} /></button>
          </div>
        </div>
      </aside>

      <div className="flex-1 min-w-0">
        {/* Superadmin impersonation banner — only shows when we're viewing an
            org we don't own, so it's obvious we're not the merchant. */}
        {user.is_superadmin && activeOrg?._superadmin_view && (
          <div className="sticky top-0 z-30 bg-[#6474FF] text-white flex items-center justify-between px-5 sm:px-8 h-11 text-[13px]" data-testid="impersonation-banner">
            <div className="flex items-center gap-2.5 min-w-0">
              <ShieldCheck size={14} strokeWidth={2.4} />
              <span className="truncate"><span className="text-white/70">Managing merchant:</span> <strong className="font-semibold">{activeOrg.name}</strong></span>
            </div>
            <button data-testid="impersonation-exit" onClick={() => { exitImpersonation(); nav("/app/admin"); }} className="inline-flex items-center gap-1.5 hover:opacity-80 font-medium">
              Exit <XIcon size={13} />
            </button>
          </div>
        )}
        <div className="lg:hidden sticky top-0 z-30 bg-[#0B0D10] text-white flex items-center justify-between px-4 h-14">
          <div className="flex items-center gap-2"><BrandMark size={24} tone="light" /><span className="font-display font-extrabold text-sm">BackPocket</span></div>
          <button data-testid="mobile-menu" onClick={() => setOpen(!open)}><Menu size={22} /></button>
        </div>
        {!isSalesOnly && (
          <div className="hidden lg:flex sticky top-0 z-20 h-12 items-center justify-between px-8 bg-[#F4F1E9]/85 backdrop-blur-md border-b border-[#0B0D10]/[0.06]" data-testid="app-topbar">
            <div className="flex items-center gap-2 font-mono text-[10.5px] uppercase tracking-[0.18em] text-[#0B0D10]/45">
              <span className="truncate max-w-[220px]">{activeOrg?.name || "BackPocket"}</span><span>/</span><span className="text-[#0B0D10]">{pageLabel}</span>
            </div>
            <Link to="/app/scanner" data-testid="topbar-scan" className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-[#0B0D10] text-white text-[12px] font-semibold hover:bg-black transition-colors"><ScanLine size={13} />Scan</Link>
          </div>
        )}
        <motion.main initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="p-5 sm:p-8 max-w-[1240px] mx-auto">{children}</motion.main>
      </div>
      <FirstRunTour />
    </div>
  );
}

export function PageHeader({ title, subtitle, action }) {
  return (
    <div className="flex items-start justify-between gap-4 mb-6">
      <div>
        <h1 className="font-display text-[24px] sm:text-[30px] font-extrabold tracking-[-0.02em] leading-[1.05] text-[#0B0D10]">{title}</h1>
        {subtitle && <p className="text-[#0B0D10]/55 mt-1.5 text-[13.5px] leading-relaxed max-w-2xl">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}
